import {
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
  useMsal,
} from '@azure/msal-react';
import type { ReactNode } from 'react';

import { loginRequest } from '~lib/auth/msal-config';

import { Button } from '~ui/base/button';
import { Logo } from '~ui/app/branding/logo';
import { BalancedMainHeading } from '~ui/app/headings/balanced-main-heading';
import { Typography } from '~ui/base/typography';

interface AppRootProps {
  children: ReactNode;
}

// Minimal page shell with a sign-in gate. Header matches the dataset-search
// frontend treatment; the unauthenticated state is a single hero card with
// a primary sign-in button.
function AppRoot({ children }: AppRootProps) {
  return (
    <div className="min-h-full bg-background">
      <header className="border-b border-border/60 bg-background/80 backdrop-blur-sm">
        <div className="mx-auto flex max-w-canvas items-center justify-between px-8 py-5">
          <Logo />
          <Typography
            variant="caption-md"
            as="span"
            className="text-muted-foreground"
          >
            Dataset Catalog
          </Typography>
        </div>
      </header>
      <main className="mx-auto max-w-canvas px-8 py-10">
        <AuthenticatedTemplate>{children}</AuthenticatedTemplate>
        <UnauthenticatedTemplate>
          <SignInPrompt />
        </UnauthenticatedTemplate>
      </main>
    </div>
  );
}

function SignInPrompt() {
  const { instance } = useMsal();

  return (
    <div className="mx-auto mt-16 flex max-w-xl flex-col items-start gap-y-6">
      <BalancedMainHeading>Sign in to browse datasets</BalancedMainHeading>
      <Typography variant="body-lg-extralight" className="text-muted-foreground">
        This catalogue lists the shared datasets stored in Azure Blob Storage.
        Sign in with your SENSCIENCE account to continue.
      </Typography>
      <Button onClick={() => instance.loginRedirect(loginRequest)}>
        Sign in with Microsoft
      </Button>
    </div>
  );
}

export { AppRoot };
